/**
 * Plan 12, Task 6.2 — Cloud layer mesh using cloud-noise shader.
 *
 * Builds a large horizontal plane with the cloud-noise shader applied.
 * Positioned below the islands to create a "clouds below islands" effect (spec §8).
 */

import * as THREE from 'three';
import type { ShaderRegistry } from '../shaders/types';

export type CloudLayerShape = 'plane' | 'snow_globe';

export interface CloudLayerConfig {
  readonly shape?: CloudLayerShape;
  readonly color?: THREE.ColorRepresentation;
  readonly opacity?: number;
  readonly density?: number;
  readonly noiseScale?: number;
  readonly drift?: THREE.Vector2 | { readonly x: number; readonly y: number };
  readonly height?: number;
  readonly size?: number;
}

export interface CloudLayerResult {
  /** The cloud mesh. Add to scene groups.environment. */
  readonly mesh: THREE.Mesh;
  /** Per-frame update: advances uTime for cloud drift animation. */
  update(deltaSeconds: number): void;
  /** Dispose geometry + material. */
  dispose(): void;
}

export const CLOUD_BASE_SIZE_USER_DATA_KEY = 'cloudBaseSize';
export const SNOW_GLOBE_RADIAL_SEGMENTS = 64;
export const SNOW_GLOBE_BOTTOM_RINGS = 8;
export const SNOW_GLOBE_SIDE_RINGS = 6;
export const SNOW_GLOBE_CAP_RINGS = 8;
export const SNOW_GLOBE_TRIANGLE_BUDGET = 3_000;

export function getCloudLayerBaseSize(mesh: THREE.Mesh): number {
  const value = mesh.userData[CLOUD_BASE_SIZE_USER_DATA_KEY];
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;

  const geometry = mesh.geometry as THREE.PlaneGeometry & {
    parameters?: { width?: number };
  };
  return geometry.parameters?.width ?? 120;
}

export function getCloudLayerBoundaryRadius(mesh: THREE.Mesh): number {
  const baseRadius = getCloudLayerBaseSize(mesh) / 2;
  const horizontalScale = Math.max(Math.abs(mesh.scale.x), Math.abs(mesh.scale.z));
  return baseRadius * horizontalScale;
}

function setCloudLayerBaseSize(mesh: THREE.Mesh, size: number): void {
  mesh.userData[CLOUD_BASE_SIZE_USER_DATA_KEY] = size;
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function smootherstep(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function makeSnowGlobeCloudGeometry(size: number): THREE.BufferGeometry {
  const radius = size / 2;
  const sideHeight = radius * 0.68;
  const capHeight = radius * 1.16;
  const lowerRings = SNOW_GLOBE_BOTTOM_RINGS + SNOW_GLOBE_SIDE_RINGS;
  const flatCoreRatio = 0.32;
  const ringProfiles: { radius: number; y: number }[] = [];

  for (let i = 0; i <= lowerRings; i += 1) {
    const radiusT = i / lowerRings;
    const liftT = THREE.MathUtils.clamp((radiusT - flatCoreRatio) / (1 - flatCoreRatio), 0, 1);
    ringProfiles.push({
      radius: radius * radiusT,
      y: sideHeight * smootherstep(liftT),
    });
  }

  for (let i = 1; i <= SNOW_GLOBE_CAP_RINGS; i += 1) {
    const t = i / SNOW_GLOBE_CAP_RINGS;
    ringProfiles.push({
      radius: THREE.MathUtils.lerp(radius, 0, smoothstep(t)),
      y: THREE.MathUtils.lerp(sideHeight, capHeight, smoothstep(t)),
    });
  }

  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const verticesPerRing = SNOW_GLOBE_RADIAL_SEGMENTS + 1;

  for (const profile of ringProfiles) {
    for (let segment = 0; segment <= SNOW_GLOBE_RADIAL_SEGMENTS; segment += 1) {
      const angle = (segment / SNOW_GLOBE_RADIAL_SEGMENTS) * Math.PI * 2;
      const x = Math.cos(angle) * profile.radius;
      const z = Math.sin(angle) * profile.radius;
      positions.push(x, profile.y, z);
      uvs.push(x / size + 0.5, z / size + 0.5);
    }
  }

  for (let ring = 0; ring < ringProfiles.length - 1; ring += 1) {
    const current = ring * verticesPerRing;
    const next = (ring + 1) * verticesPerRing;
    for (let segment = 0; segment < SNOW_GLOBE_RADIAL_SEGMENTS; segment += 1) {
      const a = current + segment;
      const b = current + segment + 1;
      const c = next + segment;
      const d = next + segment + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();
  return geometry;
}

/**
 * Build a large cloud layer with cloud-noise shader ("clouds below islands" per spec §8).
 * Plane shape preserves legacy behaviour. Snow-globe shape wraps the cloud field upward
 * around the board and closes overhead while keeping `height` as the lowest cloud Y.
 */
export function buildCloudLayer(
  shaderRegistry: ShaderRegistry,
  config?: CloudLayerConfig,
): CloudLayerResult {
  const size = config?.size ?? 120;
  const shape = config?.shape ?? 'plane';
  const geometry =
    shape === 'snow_globe'
      ? makeSnowGlobeCloudGeometry(size)
      : new THREE.PlaneGeometry(size, size, 1, 1);

  // ── Material ─────────────────────────────────────────────────────────────
  // Cloud-noise shader already sets side: DoubleSide, transparent: true, depthWrite: false.
  const cloudFactory = shaderRegistry.get('cloud-noise');
  const material = cloudFactory();
  const uniforms = material.uniforms;
  if (config?.color != null && uniforms['uCloudColor']?.value instanceof THREE.Color) {
    uniforms['uCloudColor'].value.set(config.color);
  }
  if (config?.opacity != null && uniforms['uOpacity'] != null) {
    uniforms['uOpacity'].value = config.opacity;
  }
  if (config?.density != null && uniforms['uDensity'] != null) {
    uniforms['uDensity'].value = config.density;
  }
  if (config?.noiseScale != null && uniforms['uNoiseScale'] != null) {
    uniforms['uNoiseScale'].value = config.noiseScale;
  }
  if (config?.drift != null && uniforms['uDrift']?.value instanceof THREE.Vector2) {
    uniforms['uDrift'].value.set(config.drift.x, config.drift.y);
  }

  // ── Mesh ─────────────────────────────────────────────────────────────────
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = shape === 'snow_globe' ? 'snow-globe-cloud-layer' : 'base-cloud-layer';
  mesh.position.set(0, config?.height ?? -5, 0);
  setCloudLayerBaseSize(mesh, size);
  if (shape === 'plane') {
    // Rotate so the plane faces up (PlaneGeometry is in XY plane by default)
    mesh.rotation.x = -Math.PI / 2;
  } else {
    material.side = THREE.BackSide;
  }

  // ── State ─────────────────────────────────────────────────────────────────
  let disposed = false;

  return {
    mesh,

    update(deltaSeconds: number): void {
      material.uniforms.uTime.value += deltaSeconds;
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      geometry.dispose();
      material.dispose();
    },
  };
}
